/* eslint-disable max-lines */
import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { ReactEditor } from 'slate-react';
import { Editor, Node, Text, Transforms, Range, Path, NodeEntry } from 'slate';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { DocumentTranscriptTextV1 } from '@utils/types/editors/transcripts/transcript.ts';
import {
  replaceSpeakersInEditorChildren,
  renameSpeakerByTimelineInEditorChildren
} from '@utils/editors/replace-speakers-in-editor-children.ts';
import { VoiceTimelineSpan } from '@utils/types/organization/voice-profile.ts';

interface EditorChange {
  editor: ReactEditor;
  timestamp: number;
  undone: boolean;
}

interface EditorState {
  editors: Record<string, ReactEditor | null>;
  editorChanges: EditorChange[];
  currentChangeIndex: number;
}

type EditorAction =
  | { type: 'ADD_EDITOR'; payload: { id: string; editor: ReactEditor } }
  | { type: 'REMOVE_EDITOR'; payload: { id: string } }
  | { type: 'ADD_EDITOR_CHANGE'; payload: { editor: ReactEditor } }
  | { type: 'MARK_CHANGE_UNDONE'; payload: { index: number } }
  | { type: 'MARK_CHANGE_REDONE'; payload: { index: number } }
  | { type: 'UPDATE_CURRENT_INDEX'; payload: { index: number } };

const initialState: EditorState = {
  editors: {},
  editorChanges: [],
  currentChangeIndex: -1
};

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'ADD_EDITOR':
      return {
        ...state,
        editors: {
          ...state.editors,
          [action.payload.id]: action.payload.editor
        }
      };
    case 'REMOVE_EDITOR': {
      const updatedEditors = { ...state.editors };
      delete updatedEditors[action.payload.id];
      return { ...state, editors: updatedEditors };
    }
    case 'ADD_EDITOR_CHANGE': {
      const TIME_THRESHOLD = 2000; // 2 seconds
      if (
        state.editorChanges.length === 0 ||
        state.editorChanges[0].editor !== action.payload.editor ||
        Date.now() - state.editorChanges[0].timestamp > TIME_THRESHOLD
      ) {
        return {
          ...state,
          editorChanges: [
            {
              editor: action.payload.editor,
              timestamp: Date.now(),
              undone: false
            },
            ...state.editorChanges
          ],
          currentChangeIndex: state.currentChangeIndex + 1
        };
      }
      return state;
    }
    case 'MARK_CHANGE_UNDONE':
      return {
        ...state,
        editorChanges: state.editorChanges.map((change, index) =>
          index === action.payload.index ? { ...change, undone: true } : change
        )
      };
    case 'MARK_CHANGE_REDONE':
      return {
        ...state,
        editorChanges: state.editorChanges.map((change, index) =>
          index === action.payload.index ? { ...change, undone: false } : change
        )
      };
    case 'UPDATE_CURRENT_INDEX':
      return {
        ...state,
        currentChangeIndex: action.payload.index
      };
    default:
      return state;
  }
}

const EditorContext = createContext<{
  editors: Record<string, ReactEditor | null>;
  addEditor: (id: string, editor: ReactEditor) => void;
  removeEditor: (id: string) => void;
  replaceOneWordInEditor: (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => void;
  replaceAllWordsInEditor: (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => void;
  goToNextMatch: (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    start?: number,
    stop?: number
  ) => void;
  goToPreviousMatch: (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    start?: number,
    stop?: number
  ) => void;
  undo: () => void;
  redo: () => void;
  trackModification: (editor: ReactEditor) => void;
  replaceSpeakersInEditor: (oldSpeaker: string, newSpeaker: string) => void;
  /** Name the rows of one detected voice, located by the source timeline. */
  renameSpeakerByTimelineInEditor: (
    timeline: VoiceTimelineSpan[],
    speakerLabel: string,
    newSpeaker: string
  ) => void;

  /** NEW: precise single-hit replacement by timestamps + offsets */
  replaceInEditorAtTimestampAndOffsets: (
    start: number,
    stop: number,
    from: number,
    to: number,
    replacement?: string
  ) => void;
}>({
  editors: {},
  addEditor: () => {},
  removeEditor: () => {},
  replaceOneWordInEditor: () => {},
  replaceAllWordsInEditor: () => {},
  goToNextMatch: () => {},
  goToPreviousMatch: () => {},
  undo: () => {},
  redo: () => {},
  trackModification: () => {},
  replaceSpeakersInEditor: () => {},
  renameSpeakerByTimelineInEditor: () => {},
  replaceInEditorAtTimestampAndOffsets: () => {}
});

export const EditorProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(editorReducer, initialState);
  const { currentMatch, setCurrentMatch, undo, redo } = useTranscript();

  const addEditor = (id: string, editor: ReactEditor) => {
    dispatch({
      type: 'ADD_EDITOR',
      payload: { id: `editor-${id}`, editor }
    });
  };

  const removeEditor = (id: string) => {
    dispatch({ type: 'REMOVE_EDITOR', payload: { id: `editor-${id}` } });
  };

  const findMatches = (
    editor: Editor,
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean
  ) => {
    if (!searchText) return [];

    const matches: Range[] = [];
    const flags = caseSensitive ? 'g' : 'gi';
    const searchQuery = wholeWord ? `\\b${searchText}\\b` : searchText;
    const regex = new RegExp(searchQuery, flags);

    for (const [node, path] of Node.texts(editor)) {
      if (Text.isText(node)) {
        const text = node.text;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null) {
          const start = match.index;
          const end = start + match[0].length;
          matches.push({
            anchor: { path, offset: start },
            focus: { path, offset: end }
          });
        }
      }
    }
    return matches;
  };

  const handleReplaceCurrent = (
    editor: Editor,
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => {
    const matches = findMatches(editor, searchText, caseSensitive, wholeWord);
    if (matches && matches.length > 0) {
      Transforms.select(editor, matches[0]);
      Transforms.insertText(editor, replacement || '');
      handleNextMatchWithEditor(editor, matches);
    }
  };

  const handleReplaceAll = (
    editor: Editor,
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => {
    const matches = findMatches(editor, searchText, caseSensitive, wholeWord);
    if (matches.length === 0) return;

    // Reverse iteration prevents path invalidation when replacing text
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      Transforms.select(editor, match);
      Transforms.insertText(editor, replacement || '');
    }
  };

  const handleNextMatchWithEditor = (editor: Editor, matches: Range[]) => {
    if (matches.length === 0) return;

    const currentIndex = matches.findIndex(match => {
      const [node] = Editor.node(editor, match.anchor);
      const nodeAsText = node as DocumentTranscriptTextV1;
      return (
        nodeAsText.start === (currentMatch?.start || 0) &&
        nodeAsText.stop === (currentMatch?.stop || 0)
      );
    });

    const nextIndex = (currentIndex + 1) % matches.length;
    const nextMatch = matches[nextIndex];

    const [node] = Editor.node(editor, nextMatch.anchor);
    setCurrentMatch(node as DocumentTranscriptTextV1);

    Transforms.select(editor, nextMatch);
  };

  const getEditorAndFindMatches = useMemo(() => {
    return (
      searchText: string,
      caseSensitive: boolean,
      wholeWord: boolean,
      start?: number,
      stop?: number
    ): [ReactEditor | null, Range[]] => {
      if (start == null || stop == null) {
        for (const editor of Object.values(state.editors)) {
          if (!editor) continue;
          const matches = findMatches(
            editor,
            searchText,
            caseSensitive,
            wholeWord
          );
          if (matches.length > 0) {
            return [editor, matches];
          }
        }
        return [null, []];
      }

      for (const editor of Object.values(state.editors)) {
        if (!editor) continue;

        const matches: Range[] = [];
        for (const [node, path] of Node.texts(editor)) {
          const nodeAsText = node as DocumentTranscriptTextV1;
          if (
            Text.isText(node) &&
            nodeAsText.start != null &&
            nodeAsText.stop != null &&
            nodeAsText.start >= start &&
            nodeAsText.stop <= stop
          ) {
            matches.push({
              anchor: { path, offset: 0 },
              focus: { path, offset: node.text.length }
            });
          }
        }

        if (matches.length > 0) {
          return [editor, matches];
        }
      }

      return [null, []];
    };
  }, [state.editors, currentMatch, setCurrentMatch]);

  const goToNextMatch = (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    start?: number,
    stop?: number
  ) => {
    const [editor, matches] = getEditorAndFindMatches(
      searchText,
      caseSensitive,
      wholeWord,
      start,
      stop
    );
    if (!editor || matches.length === 0) return;

    const currentIndex = matches.findIndex(match => {
      const [node] = Editor.node(editor, match.anchor);
      const nodeAsText = node as DocumentTranscriptTextV1;
      return (
        nodeAsText.start === (currentMatch?.start || 0) &&
        nodeAsText.stop === (currentMatch?.stop || 0)
      );
    });

    const nextIndex = (currentIndex + 1) % matches.length;
    const nextMatch = matches[nextIndex];

    const [node] = Editor.node(editor, nextMatch.anchor);
    setCurrentMatch(node as DocumentTranscriptTextV1);

    Transforms.select(editor, nextMatch);
  };

  const goToPreviousMatch = (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    start?: number,
    stop?: number
  ) => {
    const [editor, matches] = getEditorAndFindMatches(
      searchText,
      caseSensitive,
      wholeWord,
      start,
      stop
    );
    if (!editor || matches.length === 0 || !currentMatch) return;

    const currentIndex = matches[0].anchor.path[0];
    const prevIndex =
      currentIndex === -1
        ? matches.length - 1
        : (currentIndex - 1 + matches.length) % matches.length;
    const prevMatch = matches[prevIndex];

    const [node] = Editor.node(editor, prevMatch.anchor);
    setCurrentMatch(node as DocumentTranscriptTextV1);

    Transforms.select(editor, prevMatch);
  };

  const trackModification = (editor: ReactEditor) => {
    dispatch({
      type: 'ADD_EDITOR_CHANGE',
      payload: { editor }
    });
  };

  const handleUndo = () => {
    // Delegate to TranscriptContext's undo
    undo();
  };

  const handleRedo = () => {
    // Delegate to TranscriptContext's redo
    redo();
  };

  const replaceOneWordInEditor = (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => {
    for (const editor of Object.values(state.editors)) {
      if (editor) {
        handleReplaceCurrent(
          editor,
          searchText,
          caseSensitive,
          wholeWord,
          replacement
        );
      }
    }
  };

  const replaceAllWordsInEditor = (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => {
    for (const editor of Object.values(state.editors)) {
      if (editor) {
        handleReplaceAll(
          editor,
          searchText,
          caseSensitive,
          wholeWord,
          replacement
        );
      }
    }
  };

  const replaceSpeakersInEditor = (oldSpeaker: string, newSpeaker: string) => {
    for (const editor of Object.values(state.editors)) {
      if (editor) {
        replaceSpeakersInEditorChildren(editor, oldSpeaker, newSpeaker);
      }
    }
  };

  const renameSpeakerByTimelineInEditor = (
    timeline: VoiceTimelineSpan[],
    speakerLabel: string,
    newSpeaker: string
  ) => {
    for (const editor of Object.values(state.editors)) {
      if (editor) {
        renameSpeakerByTimelineInEditorChildren(
          editor,
          timeline,
          speakerLabel,
          newSpeaker
        );
      }
    }
  };

  /** Prefer a focused editor; else first non-null editor */
  const getActiveEditor = (): ReactEditor | null => {
    for (const e of Object.values(state.editors)) {
      try {
        if (e && ReactEditor.isFocused(e)) return e;
      } catch {
        // Silently handle Slate DOM resolution errors
      }
    }
    for (const e of Object.values(state.editors)) {
      if (e) return e;
    }
    return null;
  };

  /** Resolve the Slate path of a text node by transcript timestamps */
  const findTextPathByTimestamps = (
    editor: ReactEditor,
    start: number,
    stop: number
  ): Path | null => {
    let found: NodeEntry | null = null;
    for (const entry of Node.texts(editor)) {
      const [node, path] = entry;
      const n = node as unknown as DocumentTranscriptTextV1;
      if (Text.isText(node) && n.start === start && n.stop === stop) {
        found = [node, path];
        break;
      }
    }
    return found ? (found[1] as Path) : null;
  };

  const replaceInEditorAtTimestampAndOffsets = (
    start: number,
    stop: number,
    from: number,
    to: number,
    replacement?: string
  ) => {
    const editor = getActiveEditor();
    if (!editor) return;

    const path = findTextPathByTimestamps(editor, start, stop);
    if (!path) return;

    Transforms.select(editor, {
      anchor: { path, offset: from },
      focus: { path, offset: to }
    });
    Transforms.insertText(editor, replacement ?? '');

    trackModification(editor);

    try {
      ReactEditor.focus(editor);
    } catch {
      // Silently handle Slate DOM resolution errors
    }
  };

  return (
    <EditorContext.Provider
      value={{
        editors: state.editors,
        addEditor,
        removeEditor,
        replaceOneWordInEditor,
        replaceAllWordsInEditor,
        goToNextMatch,
        goToPreviousMatch,
        undo: handleUndo,
        redo: handleRedo,
        trackModification,
        replaceSpeakersInEditor,
        renameSpeakerByTimelineInEditor,
        replaceInEditorAtTimestampAndOffsets
      }}
    >
      {children}
    </EditorContext.Provider>
  );
};

export const useEditorContext = () => useContext(EditorContext);
